import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import { decimal } from 'Helpers/currencyFormat';

import Button from 'CommonComponents/Button';

const SUMMARISE_THRESHOLD = 10;

/**
 * QuantityBulkImpactPreviewDialog
 *
 * Shows a consolidated downstream impact preview before a bulk source import is committed.
 *
 * Unlike QuantityImpactPreviewDialog (which fetches its own data for a single source),
 * this dialog receives pre-computed impact data that was already fetched server-side
 * by the import-page intercept script.
 *
 * Lifecycle:
 *   1. Import form submit is intercepted by inline JS on the import Blade view.
 *   2. JS POSTs the file to the bulk-impact-preview endpoint and receives {sources, totals}.
 *   3. If sources is non-empty, JS dispatches `quantitybulkimpact:check` with:
 *      { previewData: {sources, totals}, onConfirm: fn, onCancel?: fn }
 *   4. This component shows the consolidated dialog grouped by source.
 *   5. User confirms → onConfirm() called → form submitted.
 *   6. User cancels → dialog closes, form submit is blocked.
 */
const QuantityBulkImpactPreviewDialog = () => {
    const [state, setState] = useState(null); // null = hidden
    const [expandedSources, setExpandedSources] = useState({});

    const handleCheck = useCallback((e) => {
        const { previewData, onConfirm, onCancel } = e.detail || {};
        if (!previewData || !onConfirm) return;
        if (!previewData.sources || previewData.sources.length === 0) {
            // No dependents — proceed immediately
            onConfirm();
            return;
        }
        setState({ previewData, onConfirm, onCancel: onCancel ?? null });
        setExpandedSources({});
    }, []);

    useEffect(() => {
        window.addEventListener('quantitybulkimpact:check', handleCheck);
        return () => window.removeEventListener('quantitybulkimpact:check', handleCheck);
    }, [handleCheck]);

    const handleConfirm = () => {
        const { onConfirm } = state || {};
        setState(null);
        if (onConfirm) onConfirm();
    };

    const handleCancel = () => {
        const { onCancel } = state || {};
        setState(null);
        setExpandedSources({});
        if (onCancel) onCancel();
    };

    if (!state) return null;

    const { previewData } = state;
    const { sources, totals } = previewData;

    const formatCurrency = (value) => (value !== null && value !== undefined ? decimal(value, 2) : '—');

    const formatDelta = (value) => {
        if (value === null || value === undefined) return '—';
        const sign = value > 0 ? '+' : '';
        return `${sign}${formatCurrency(value)}`;
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return '—';
        const sign = value > 0 ? '+' : '';
        return `${sign}${value}%`;
    };

    const toggleExpand = (idx) => {
        setExpandedSources((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    return (
        <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
            <div className="tw-bg-white tw-rounded-lg tw-shadow tw-w-full tw-max-w-4xl tw-max-h-[90vh] tw-flex tw-flex-col">
                {/* Header */}
                <div className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-x-4 tw-p-4 tw-border-b">
                    <h3 className="tw-text-2xl tw-font-semibold tw-text-blue">{$t('Downstream Impact Preview')}</h3>
                    <svg
                        onClick={handleCancel}
                        className="tw-w-4 tw-h-4 tw-text-charcoal-75 hover:tw-text-charcoal tw-cursor-pointer tw-flex-shrink-0 tw-self-start tw-mt-1"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 14 14"
                    >
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                    </svg>
                </div>

                {/* Body */}
                <div className="tw-overflow-y-auto tw-flex-1 tw-p-4 tw-space-y-4">
                    {/* Summary banner */}
                    <div className="tw-bg-blue-50 tw-rounded-lg tw-p-3">
                        <p className="tw-text-sm tw-text-blue-800">
                            {$t('This import will change {{count}} source(s) and affect {{lines}} linked budget line(s).', {
                                count: sources.length,
                                lines: totals.total_dependents,
                            })}
                            {totals.total_delta !== undefined && (
                                <>
                                    {' '}
                                    {$t('Total forecast change')}: <strong>{formatDelta(totals.total_delta)}</strong>
                                </>
                            )}
                        </p>
                    </div>

                    {totals.any_over_committed && (
                        <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-3">
                            <p className="tw-text-sm tw-text-amber-800 tw-font-medium">
                                {$t('Warning: one or more budget lines will fall below their committed amount after this change.')}
                            </p>
                        </div>
                    )}

                    {/* Per-source sections */}
                    {sources.map((impact, idx) => {
                        const { source, dependents, summary } = impact;
                        const isExpanded = expandedSources[idx];
                        const visibleDependents =
                            isExpanded || dependents.length <= SUMMARISE_THRESHOLD ? dependents : dependents.slice(0, SUMMARISE_THRESHOLD);

                        return (
                            <div key={idx} className="tw-border tw-rounded-lg tw-overflow-hidden">
                                {/* Source header */}
                                <div className="tw-bg-gray-50 tw-px-4 tw-py-2 tw-border-b">
                                    <p className="tw-text-sm tw-text-charcoal">
                                        <strong>{source.label}</strong> {$t('from')} <strong>{formatCurrency(source.old_value)}</strong> {$t('to')}{' '}
                                        <strong>{formatCurrency(source.new_value)}</strong>
                                        {' — '}
                                        <span className="tw-text-charcoal-50">
                                            {$t('{{count}} linked budget line(s)', { count: summary.total_dependents })}
                                        </span>
                                    </p>
                                </div>

                                {/* Dependents table */}
                                <div className="tw-overflow-x-auto tw-px-4 tw-py-2">
                                    <table className="tw-w-full tw-text-sm tw-border-collapse">
                                        <thead>
                                            <tr className="tw-border-b tw-text-left">
                                                <th className="tw-py-2 tw-pr-3 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-uppercase tw-tracking-wide">
                                                    {$t('Budget Line')}
                                                </th>
                                                <th className="tw-py-2 tw-px-3 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-uppercase tw-tracking-wide tw-text-right">
                                                    {$t('Current')}
                                                </th>
                                                <th className="tw-py-2 tw-px-3 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-uppercase tw-tracking-wide tw-text-right">
                                                    {$t('New')}
                                                </th>
                                                <th className="tw-py-2 tw-pl-3 tw-text-xs tw-font-semibold tw-text-charcoal-50 tw-uppercase tw-tracking-wide tw-text-right">
                                                    {$t('Change')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleDependents.map((dep, i) => (
                                                <tr key={i} className={`tw-border-b tw-border-gray-100 ${dep.over_committed ? 'tw-bg-amber-50' : ''}`}>
                                                    <td className="tw-py-2 tw-pr-3 tw-text-charcoal">
                                                        {dep.name}
                                                        {dep.over_committed && (
                                                            <span
                                                                className="tw-ml-1 tw-text-xs tw-text-amber-700 tw-font-medium"
                                                                title={$t('New forecast falls below committed amount')}
                                                            >
                                                                &#9888;
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="tw-py-2 tw-px-3 tw-text-right tw-text-charcoal-75 tw-tabular-nums">
                                                        {formatCurrency(dep.current_forecast)}
                                                    </td>
                                                    <td className="tw-py-2 tw-px-3 tw-text-right tw-text-charcoal-75 tw-tabular-nums">
                                                        {formatCurrency(dep.new_forecast)}
                                                    </td>
                                                    <td
                                                        className={`tw-py-2 tw-pl-3 tw-text-right tw-tabular-nums tw-font-medium ${dep.delta_amount > 0 ? 'tw-text-green-700' : dep.delta_amount < 0 ? 'tw-text-red-600' : 'tw-text-charcoal-50'}`}
                                                    >
                                                        {formatDelta(dep.delta_amount)}
                                                        {dep.delta_percent !== null && (
                                                            <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-charcoal-50">
                                                                ({formatPercent(dep.delta_percent)})
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {!isExpanded && dependents.length > SUMMARISE_THRESHOLD && (
                                                <tr>
                                                    <td colSpan={4} className="tw-py-2 tw-text-center">
                                                        <button
                                                            type="button"
                                                            className="tw-text-sm tw-text-blue-600 hover:tw-text-blue-800 hover:tw-underline"
                                                            onClick={() => toggleExpand(idx)}
                                                        >
                                                            {$t('Show all {{count}} expense lines', { count: dependents.length })}
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                        {dependents.length > 1 && (
                                            <tfoot>
                                                <tr className="tw-border-t-2 tw-border-gray-300">
                                                    <td className="tw-py-2 tw-pr-3 tw-text-xs tw-font-semibold tw-text-charcoal-75">{$t('Total impact')}</td>
                                                    <td colSpan={2}></td>
                                                    <td
                                                        className={`tw-py-2 tw-pl-3 tw-text-right tw-tabular-nums tw-font-semibold ${summary.total_delta > 0 ? 'tw-text-green-700' : summary.total_delta < 0 ? 'tw-text-red-600' : 'tw-text-charcoal-50'}`}
                                                    >
                                                        {formatDelta(summary.total_delta)}
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="tw-p-4 tw-border-t tw-flex tw-items-center tw-justify-between">
                    <p className="tw-text-xs tw-text-charcoal-50">{$t('The linked quantities will update automatically after the import completes.')}</p>
                    <div className="modal__footer">
                        <Button variant="secondary" onClick={handleCancel}>
                            {$t('Cancel')}
                        </Button>
                        <Button variant="primary" onClick={handleConfirm}>
                            {$t('Proceed with Import')}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const rootEl = document.getElementById('QuantityBulkImpactPreviewApp');
if (rootEl) {
    ReactDOM.render(
        <App>
            <QuantityBulkImpactPreviewDialog />
        </App>,
        rootEl,
    );
}
